import {ActionIcon, Button, createStyles, Group, Loader, Popover, Stack, Text, Textarea} from '@mantine/core';
import React, {KeyboardEvent, useCallback, useEffect, useRef, useState} from 'react';

import {useChatInputControls} from 'llm/chat-library/ChatInput';
import {useMessageThread} from 'llm/chat-library/useMessageThread';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import createChatForPipelineStep from './createChatForPipelineStep';
import {useGeneratedCustomCode} from './useGeneratedCustomCode';

interface PipelineCustomCodeWriterButtonProps {
  pipelineStepId: string;
  disabled?: boolean;
  /** Called with the agent's latest generated code so the editor can overwrite its contents. */
  onGeneratedCode: (code: string) => void;
}

const useStyles = createStyles((theme) => ({
  dropdown: {
    width: 340,
  },
  warning: {
    color: theme.colors.gray[6],
  },
}));

interface PipelineCustomCodeWriterFormProps {
  chatSessionId: string;
  onGeneratedCode: (code: string) => void;
}

/**
 * Headless Custom Code Writer engine plus its query input. Runs the Pipelines Custom Code Writer agent
 * for {@link chatSessionId} without rendering a message thread: the user types a single query, submits,
 * and the agent's generated code is surfaced via {@link onGeneratedCode} so the editor can overwrite its
 * contents.
 *
 * This component keeps `useMessageThread` mounted (via the parent Popover's `keepMounted`) so the code
 * keeps flowing to the editor even after the bubble is closed.
 */
function PipelineCustomCodeWriterForm({
  chatSessionId,
  onGeneratedCode,
}: PipelineCustomCodeWriterFormProps): JSX.Element {
  const {classes} = useStyles();

  const {inputText, setInputText} = useChatInputControls(chatSessionId);
  const {progressData, isAssistantRunning, sendMessage} = useMessageThread({
    chatSessionId,
    includeToolCalls: true,
  });

  // Watch the agent's tool calls for the latest generated code and push it to the editor, overwriting
  // whatever is currently there. The hook only returns code once the agent has stopped running. Track
  // the last value written so we only fire when the code changes.
  const generatedCode = useGeneratedCustomCode(progressData, isAssistantRunning);
  const lastWrittenCodeRef = useRef<string | null>(null);
  useEffect(() => {
    if (generatedCode != null && generatedCode !== lastWrittenCodeRef.current) {
      lastWrittenCodeRef.current = generatedCode;
      onGeneratedCode(generatedCode);
    }
  }, [generatedCode, onGeneratedCode]);

  const canSubmit = inputText.trim().length > 0 && !isAssistantRunning;

  const handleSubmit = useCallback((): void => {
    if (!canSubmit) {
      return;
    }
    sendMessage();
  }, [canSubmit, sendMessage]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <Stack spacing="xs">
      <Textarea
        aria-label="Describe the code to generate"
        autosize={true}
        minRows={2}
        maxRows={8}
        placeholder="Describe the code you want to write..."
        value={inputText}
        onChange={(e) => {
          setInputText(e.currentTarget.value);
        }}
        onKeyDown={handleKeyDown}
        disabled={isAssistantRunning}
      />
      <Group position="apart" spacing="xs" noWrap={true}>
        <Text className={classes.warning} size="xs">
          AI can make mistakes. Always check results.
        </Text>
        {isAssistantRunning ? (
          <Loader size="xs" />
        ) : (
          <ActionIcon
            aria-label="Submit"
            color="blue"
            variant="filled"
            radius="xl"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            <BenchlingIcon icon="arrow-up" />
          </ActionIcon>
        )}
      </Group>
    </Stack>
  );
}

/**
 * "Write Code" button for the Custom Code editor.
 *
 * The chat session backing the agent is created lazily on first open and reused for the lifetime of the
 * editor, so follow-up queries refine the same session.
 */
export default function PipelineCustomCodeWriterButton({
  pipelineStepId,
  disabled,
  onGeneratedCode,
}: PipelineCustomCodeWriterButtonProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [isCreatingChat, setIsCreatingChat] = useState(false);

  // Lazily create the backing chat session the first time the (uncontrolled) popover opens.
  const handleOpen = async (): Promise<void> => {
    if (chatSessionId != null || isCreatingChat) {
      return;
    }
    setIsCreatingChat(true);
    try {
      const {chatSessionId: newChatSessionId} = await createChatForPipelineStep(pipelineStepId);
      setChatSessionId(newChatSessionId);
    } finally {
      setIsCreatingChat(false);
    }
  };

  return (
    <Popover
      position="bottom-end"
      withArrow={true}
      shadow="md"
      onOpen={handleOpen}
      // Keep the dropdown (and the headless message-thread engine) mounted so the agent's generated code
      // keeps flowing to the editor even while the bubble is closed.
      keepMounted={true}
    >
      <Popover.Target>
        <Button variant="secondary" size="xs" leftIcon={<BenchlingIcon icon="sparkle" />} disabled={disabled}>
          Write code
        </Button>
      </Popover.Target>
      <Popover.Dropdown className={classes.dropdown}>
        {chatSessionId != null ? (
          <PipelineCustomCodeWriterForm chatSessionId={chatSessionId} onGeneratedCode={onGeneratedCode} />
        ) : (
          <Group position="center" py="sm">
            <Loader size="sm" />
          </Group>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
